/**
 * useColorScheme Hook
 * 
 * Manages color scheme state (accent colors) and custom color support
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getStorageItem, setStorageItem } from '../services/storage.service';
import { adjustColorBrightness, generateGradient } from '../services/color.service';

export type ColorSchemeName = 'gold' | 'blue' | 'purple' | 'green' | 'red' | 'sepia' | 'custom';

export interface ColorScheme {
  primary: string;
  primaryHover: string;
  gradient: string;
  gradientHover: string;
}

export interface UseColorSchemeReturn {
  colorScheme: ColorSchemeName;
  setColorScheme: (scheme: ColorSchemeName) => void;
  setCustomColor: (color: string) => void;
  colorSchemes: Record<ColorSchemeName, ColorScheme>;
  currentScheme: ColorScheme;
}

const DEFAULT_COLOR_SCHEMES: Record<Exclude<ColorSchemeName, 'custom'>, ColorScheme> = {
  gold: {
    primary: '#D4AF37',
    primaryHover: '#B8941F',
    gradient: 'from-yellow-400 to-amber-500',
    gradientHover: 'from-yellow-500 to-amber-600'
  },
  blue: {
    primary: '#3B82F6',
    primaryHover: '#2563EB',
    gradient: 'from-blue-400 to-blue-600',
    gradientHover: 'from-blue-500 to-blue-700'
  },
  purple: {
    primary: '#8B5CF6',
    primaryHover: '#7C3AED',
    gradient: 'from-purple-400 to-purple-600',
    gradientHover: 'from-purple-500 to-purple-700'
  },
  green: {
    primary: '#10B981',
    primaryHover: '#059669',
    gradient: 'from-green-400 to-green-600',
    gradientHover: 'from-green-500 to-green-700'
  },
  red: {
    primary: '#EF4444',
    primaryHover: '#DC2626',
    gradient: 'from-red-400 to-red-600',
    gradientHover: 'from-red-500 to-red-700'
  },
  sepia: {
    primary: '#8B4513',
    primaryHover: '#6B3410',
    gradient: 'from-amber-700 to-orange-800',
    gradientHover: 'from-amber-800 to-orange-900'
  }
};

export const useColorScheme = (): UseColorSchemeReturn => {
  const [colorScheme, setColorSchemeState] = useState<ColorSchemeName>('gold');
  const [customColor, setCustomColorState] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  // Memoize color schemes (including custom if set)
  const colorSchemes = useMemo<Record<ColorSchemeName, ColorScheme>>(() => {
    const schemes = { ...DEFAULT_COLOR_SCHEMES } as Record<ColorSchemeName, ColorScheme>;
    
    if (customColor) {
      schemes.custom = {
        primary: customColor,
        primaryHover: adjustColorBrightness(customColor, -20),
        gradient: `from-[${customColor}] to-[${adjustColorBrightness(customColor, -30)}]`,
        gradientHover: `from-[${adjustColorBrightness(customColor, -10)}] to-[${adjustColorBrightness(customColor, -40)}]`
      };
    } else {
      // Provide a default custom scheme
      schemes.custom = schemes.gold;
    }
    
    return schemes;
  }, [customColor]);

  // Load color scheme from localStorage on mount
  useEffect(() => {
    const savedColorScheme = getStorageItem<ColorSchemeName>('color-scheme');
    const savedCustomColor = getStorageItem<string>('custom-color');
    
    if (savedCustomColor) {
      setCustomColorState(savedCustomColor);
    }
    
    if (savedColorScheme && Object.keys(DEFAULT_COLOR_SCHEMES).concat(['custom']).includes(savedColorScheme)) {
      setColorSchemeState(savedColorScheme);
    }
    
    setMounted(true);
  }, []);

  // Listen for theme changes and update accent color to match
  useEffect(() => {
    const handleThemeChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ theme: string }>;
      const newTheme = customEvent.detail?.theme;
      
      // Map themes to their default accent colors
      const themeToColorScheme: Record<string, ColorSchemeName> = {
        'default': 'gold',
        'sepia': 'sepia',
        'dracula': 'purple'
      };
      
      if (newTheme && themeToColorScheme[newTheme]) {
        const newColorScheme = themeToColorScheme[newTheme];
        setColorSchemeState(newColorScheme);
      }
    };
    
    window.addEventListener('themeTypeChanged', handleThemeChange);
    
    return () => {
      window.removeEventListener('themeTypeChanged', handleThemeChange);
    };
  }, []);

  // Apply color scheme to CSS variables
  const applyColorSchemeToDom = useCallback((scheme: ColorScheme, schemeName: ColorSchemeName) => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--color-primary', scheme.primary);
    root.style.setProperty('--color-primary-hover', scheme.primaryHover);
    root.style.setProperty('--gradient-primary', generateGradient(scheme.primary, scheme.primaryHover));
    root.style.setProperty('--gradient-primary-hover', generateGradient(scheme.primaryHover, adjustColorBrightness(scheme.primary, -40)));
    
    // Update classes
    root.className = root.className.replace(/color-scheme-\w+/g, '');
    root.classList.add(`color-scheme-${schemeName}`);
    root.setAttribute('data-color-scheme', schemeName);
    
    // Force style recalculation
    void document.body.offsetHeight;
    
    // Dispatch event
    window.dispatchEvent(new CustomEvent('colorSchemeChanged', { detail: { scheme: schemeName } }));
  }, []);

  // Persist and apply color scheme changes
  useEffect(() => {
    if (mounted) {
      const scheme = colorSchemes[colorScheme];
      setStorageItem('color-scheme', colorScheme);
      applyColorSchemeToDom(scheme, colorScheme);
    }
  }, [mounted, colorScheme, colorSchemes, applyColorSchemeToDom]);

  const setColorScheme = useCallback((newScheme: ColorSchemeName) => {
    setColorSchemeState(newScheme);
  }, []);

  const setCustomColor = useCallback((color: string) => {
    setCustomColorState(color);
    setStorageItem('custom-color', color);
    
    // Create custom scheme immediately
    const customScheme: ColorScheme = {
      primary: color,
      primaryHover: adjustColorBrightness(color, -20),
      gradient: `from-[${color}] to-[${adjustColorBrightness(color, -30)}]`,
      gradientHover: `from-[${adjustColorBrightness(color, -10)}] to-[${adjustColorBrightness(color, -40)}]`
    };
    
    // Apply immediately
    applyColorSchemeToDom(customScheme, 'custom');
    
    // Update state
    setColorSchemeState('custom');
  }, [applyColorSchemeToDom]);

  const currentScheme = useMemo(() => {
    return colorSchemes[colorScheme] || colorSchemes.gold;
  }, [colorSchemes, colorScheme]);

  return {
    colorScheme,
    setColorScheme,
    setCustomColor,
    colorSchemes,
    currentScheme
  };
};

